import React from 'react';
import {
  Volume2, VolumeX,
  Video, VideoOff,
  Wand2,
  MessageCircle,
  LogOut,
  SwitchCamera,
  PictureInPicture2,
  Zap,
} from 'lucide-react';

export default function Controls({
  onToggleMic,
  onToggleCamera,
  onLeave,
  onToggleChat,
  chatOpen,
  // Estado real do mic/cam do parent. Sem isso o Controls assumia true
  // por default e o paciente que entra com permissao bloqueada via os
  // botoes "Audio" e "Video" como se estivessem ativos - ele nao sabia
  // que precisava clicar pra liberar.
  micOn,
  camOn,
  // Nova API do botao Fundo: o parent decide o que acontece. Mandar
  // onToggleBackground + backgroundActive. Sem esses props o botao fica
  // escondido (paciente em desktop sem template = sem o que trocar).
  onToggleBackground,
  backgroundActive = false,
  // Camera traseira/frontal (paciente mobile, util pra mostrar lesao etc).
  onSwitchCamera,
  cameraFacing = 'user',
  // Picture-in-Picture: botao Minimizar e ACAO IMEDIATA (abre/fecha PiP na
  // hora). Sem efeito na preferencia de auto-PiP.
  onTogglePip,
  pipActive = false,
  // Auto-PiP e separado: liga/desliga a PREFERENCIA (persiste em consultas
  // futuras, abre sozinho quando trocar de aba / outro iframe pega foco).
  // Nao abre/fecha PiP na hora do clique. Parent passa null quando o fluxo
  // nao se aplica (ex: paciente, ou browser sem suporte).
  onToggleAutoPip,
  autoPipEnabled = false,
  showChat = true,
  showLeave = true,
  // Label do botao de encerrar - host encerra a sala pra todos (label
  // "Encerrar"), paciente so sai dele mesmo (label "Sair"). Default
  // "Encerrar" pra retrocompatibilidade com chamadas antigas.
  leaveLabel = 'Encerrar',
  showScreenshare = true,
  minimal = false,
  chatUnread = 0,
}) {
  // Fallback pra chamadas antigas que nao passavam micOn/camOn: assume
  // ativo (igual era o comportamento anterior).
  const isMicOn = micOn !== undefined ? micOn : true;
  const isCamOn = camOn !== undefined ? camOn : true;

  // `minimal` hides decorative UI (status badges, labels) but NOT functional buttons
  // that the caller explicitly enabled via chat=on. Otherwise the doctor ends up
  // with a minimal layout + chat=on and no way to open the chat.
  const showChatBtn = showChat;
  // `minimal` esconde so UI decorativa, nao botao funcional - igual o chat ja
  // era. Antes o medico entrava com minimal=on (URL padrao do simulador) e
  // ficava sem o botao Fundo, sem jeito de desligar/religar o template
  // durante a consulta.
  const showBackgroundBtn = !!onToggleBackground;
  const showSwitchCameraBtn = !!onSwitchCamera;
  const showPipBtn = !!onTogglePip && !minimal;
  const showAutoPipBtn = !!onToggleAutoPip && !minimal;

  return (
    // gap e padding menores no mobile pra caber 5-6 botoes em tela de 360-400px
    // sem cortar. overflow-x-auto serve de rede de seguranca caso o cliente
    // adicione algo extra depois. justify-start no mobile, center no >=sm
    // mantem o layout natural em tablet/desktop.
    <div className="flex items-end justify-start sm:justify-center gap-1 sm:gap-2 p-2 sm:p-4 bg-slate-950/90 backdrop-blur border-t border-slate-800 overflow-x-auto">
      <PillButton
        icon={isMicOn ? <Volume2 size={22} /> : <VolumeX size={22} />}
        label={isMicOn ? 'Audio' : 'Liberar microfone'}
        onClick={onToggleMic}
        tone={isMicOn ? 'neutral' : 'warn'}
      />

      <PillButton
        icon={isCamOn ? <Video size={22} /> : <VideoOff size={22} />}
        label={isCamOn ? 'Video' : 'Liberar video'}
        onClick={onToggleCamera}
        tone={isCamOn ? 'neutral' : 'warn'}
      />

      {showSwitchCameraBtn && (
        <PillButton
          icon={<SwitchCamera size={22} />}
          label={cameraFacing === 'environment' ? 'Frontal' : 'Traseira'}
          onClick={onSwitchCamera}
          tone="neutral"
        />
      )}

      {showBackgroundBtn && (
        <PillButton
          icon={<Wand2 size={22} />}
          label="Fundo"
          onClick={onToggleBackground}
          tone={backgroundActive ? 'accent' : 'neutral'}
        />
      )}

      {showChatBtn && (
        <PillButton
          icon={<MessageCircle size={22} />}
          label="Chat"
          onClick={onToggleChat}
          tone={chatOpen ? 'accent' : 'neutral'}
          badge={!chatOpen && chatUnread > 0 ? chatUnread : 0}
        />
      )}

      {showPipBtn && (
        <PillButton
          icon={<PictureInPicture2 size={22} />}
          label={pipActive ? 'Fechar' : 'Minimizar'}
          onClick={onTogglePip}
          tone={pipActive ? 'accent' : 'neutral'}
        />
      )}

      {showAutoPipBtn && (
        <PillButton
          icon={<Zap size={22} />}
          label="Auto"
          onClick={onToggleAutoPip}
          tone={autoPipEnabled ? 'accent' : 'neutral'}
        />
      )}

      {showLeave && (
        <PillButton
          icon={<LogOut size={22} />}
          label={leaveLabel}
          onClick={onLeave}
          tone="danger"
        />
      )}
    </div>
  );
}

function PillButton({ icon, label, onClick, tone = 'neutral', badge = 0 }) {
  // shrink-0 evita que o flexbox esmague o botao quando a barra nao cabe -
  // preferimos scroll horizontal a texto cortado. Padding menor no mobile.
  const base = 'shrink-0 relative flex flex-col items-center gap-0.5 sm:gap-1 px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-2xl transition-all active:scale-95 select-none';
  const tones = {
    neutral: 'bg-slate-800 hover:bg-slate-700 text-slate-100',
    accent:  'bg-emerald-600 hover:bg-emerald-500 text-white',
    warn:    'bg-amber-600 hover:bg-amber-500 text-white',
    danger:  'bg-rose-600 hover:bg-rose-500 text-white',
  };
  return (
    <button onClick={onClick} className={`${base} ${tones[tone]}`} title={label}>
      {icon}
      <span className="text-[11px] font-medium tracking-wide">{label}</span>
      {badge > 0 && (
        <span
          className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-slate-950 animate-pulse"
          aria-label={`${badge} mensagem${badge === 1 ? '' : 's'} nao lida${badge === 1 ? '' : 's'}`}
        >
          {badge > 9 ? '9+' : badge}
        </span>
      )}
    </button>
  );
}
